import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "ClientContext";
import { format, isValid } from "date-fns";
import { debounce } from "lodash";

/**
 * Hook: useGetListings
 * ---------------------
 * Fetches paginated listings data, supports search debounce,
 * and syncs results into ClientContext for UI consumption.
 *
 * DEV NOTES (for future maintenance):
 * - Review `staleTime`/`gcTime` values when tuning cache or network behavior.
 * - If adding new integration types, update `totalListingPerPage` logic.
 * - Keep `global_searh` key consistent in context; consider renaming typo to `global_search`.
 * - Payload shape must match server API v1; bump API version in constants if changed server contract.
 * - Avoid side-effects in `queryFn`; consider moving `setListingsPage` into an effect or v5 `useEffect` on `data`.
 * - Monitor debounce delay (currently 500ms) to balance UX vs request volume.
 * - Validate `SERVER_URL` env var at app startup to prevent undefined endpoint.
 * - Requirements - Cache data for 7 minutes, persit totalListing in global State.
 */

export interface ListingsData {
    listings: any[];
    listingsFound: number;
}

export interface UseListingsResult {
    listings: any[];
    listingsLoading: boolean;
    listingsError: any;
    handleSearch: () => void;
    handlePageChange: (page: number) => void;
    currentPage: number;
    totalListings: number;
    totalListingFound: number;
    totalListingPerPage: number;
}

// Ensure this env var is defined in .env and at build time.
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export const useGetListings = (): UseListingsResult => {
    // ---- CLIENT CONTEXT (slice only needed values) ----
    const {
        internal_ID,
        integration_TYPE,
        actions,
        requestType,
        global_searh,
        setGlobal_search,
        listingsPage,
        setListingsPage,
        lodgifySite,
        wix_cms_api,
    } = useClient();

    // DEV NOTE: formatDate util can be extracted to shared utils if reused elsewhere.
    const formatDate = useCallback((date: Date | number | null): string => {
        if (!date || !isValid(date)) return "";
        return format(date, "yyyy-MM-dd'T'00:00:00");
    }, []);

    // DEV NOTE: Update per-page counts if third-party API limits change.
    const totalListingPerPage = useMemo(
        () => (integration_TYPE === "lodgify" ? 12 : 9),
        [integration_TYPE]
    );

    // ---- PAGINATION STATE ----
    const [currentPage, setCurrentPage] = useState<number>(0);
    const isInitialLoadRef = useRef<boolean>(true);

    // Build query params; memoize to reduce unnecessary refetch triggers
    const queryParams = useMemo(() => {
        const base: any = {
            offset: currentPage * totalListingPerPage,
            limit: totalListingPerPage,
            action: actions.fetchListings,
            search: null,
        };

        const { checkIn, checkOut, guestsCount, location: loc = {} } = global_searh.params;

        if (global_searh.isSearching || requestType === "SEARCH_LISTINGS") {
            base.action = actions.fetchListingSearch;
            base.search = {
                guestsCount,
                checkInDateLocalized: formatDate(checkIn),
                checkOutDateLocalized: formatDate(checkOut),
                location: {
                    city: (loc as any).city || "",
                    state: (loc as any).state || "",
                    country: (loc as any).country || "",
                },
            };
        }

        return base;
    }, [
        currentPage,
        totalListingPerPage,
        actions,
        requestType,
        global_searh.isSearching,
        global_searh.params,
        formatDate,
    ]);

    // DEBUG: track param changes (remove in prod to reduce log noise)
   // useEffect(() => {
    //    console.debug("queryParams updated:", queryParams);
   // }, [queryParams]);

    // ---- DATA FETCHING ----
    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery<ListingsData, any, ListingsData>({
        queryKey: [
            "listings",
            internal_ID,
            currentPage,
            queryParams.action,
            queryParams.search,
        ],
        queryFn: async (): Promise<ListingsData> => {
            // Basic validations
            if (internal_ID === "n/a") {
                throw new Error("Invalid internal_ID");
            }
            if (integration_TYPE === "lodgify" && !lodgifySite.id)
                throw new Error("Missing Lodgify site ID");

            // Construct API payload; verify this matches server contract v1
            const payload: any = {
                action: queryParams.action,
                internal_ID,
                params: {
                    offset: queryParams.offset,
                    limit: queryParams.limit,
                    ...(queryParams.action === actions.fetchListingSearch
                        ? { search: queryParams.search, lodgifySite }
                        : { pageNum: currentPage + 1, lodgifySite }),
                },
                wix_params: {
                    wix_req: wix_cms_api.cmsRequest,
                    siteURL: wix_cms_api.cmsURL || "",
                },
            };

            const response = await axios.post(
                `${SERVER_URL}/api/controller`,
                payload
            );
            const listingData = response.data?.items ?? [];
            const totalFound = response.data.total ?? 0;

            return { listings: listingData, listingsFound: totalFound };
        },
        enabled: internal_ID !== "n/a",
        staleTime: 7 * 60_000, // 7 minutes
        gcTime: 7 * 60_000,    // 7 minutes before garbage-collection
    });
    // DEV NOTE: Only update `totalListings` on initial load, not search mode.

    useEffect(() => {
        if (data && queryParams.action === actions.fetchListings) {
            setListingsPage(prev => ({
                ...prev,
                listings: data.listings,
                totalListings: data.listingsFound,
            }));
        }
    }, [data, setListingsPage]);


    // ---- SEARCH HANDLING ----
    const debouncedSearch = useMemo(
        () =>
            debounce(() => {
                setGlobal_search(prev => ({ ...prev, isSearching: true }));
                if (!isInitialLoadRef.current) {
                    setCurrentPage(0);
                }
                isInitialLoadRef.current = false;
                // Defer refetch so context update settles first
                setTimeout(() => refetch(), 0);
            }, 500),
        [setGlobal_search, refetch]
    );

    const handleSearch = useCallback(() => {
        debouncedSearch();
    }, [debouncedSearch]);

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);
    // DEV NOTE: Provide clear return shape; keep in sync with UI consumer expectations.
    return {
        listings: data?.listings ?? [],
        listingsLoading: isLoading || isFetching,
        listingsError: error,
        handleSearch,
        handlePageChange,
        currentPage,
        totalListingPerPage,
        totalListings: listingsPage.totalListings,
        totalListingFound:
            data?.listingsFound ?? listingsPage.totalListingFound ?? 0,
    };
};
